import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Gift, Save, Search, SlidersHorizontal,
  TrendingUp, TrendingDown, Settings2, Coins,
  UserCheck, AlertCircle, Plus, Minus, ChevronLeft, ChevronRight,
  Tag, CheckCircle2, Circle, Info,
} from 'lucide-react'
import { loyaltyService } from '@/services/loyaltyService'
import { usersService } from '@/services/usersService'
import { categoriesService } from '@/services/categoriesService'
import { useDebounce } from '@/hooks/useDebounce'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import { formatCurrency } from '@/utils/formatters'

// ─── Shared style atoms ────────────────────────────────────────────────────────
const LBL = {
  display: 'block',
  fontFamily: 'Cairo, sans-serif',
  fontWeight: 700,
  fontSize: 12,
  color: '#454652',
  marginBottom: 6,
}
const INP = {
  width: '100%',
  borderRadius: 10,
  background: '#E2E2E2',
  border: 'none',
  padding: '10px 14px',
  fontSize: 14,
  fontFamily: 'Tajawal, sans-serif',
  outline: 'none',
  boxSizing: 'border-box',
  color: '#1A1C1C',
}

function SectionCard({ icon: Icon, title, subtitle, accent = '#000666', children }) {
  return (
    <div style={{
      background: '#fff',
      borderRadius: 20,
      padding: 24,
      boxShadow: '0 2px 4px rgba(26,28,28,0.04), 0 8px 16px rgba(26,28,28,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: `${accent}14`,
          color: accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={18} />
        </div>
        <div>
          <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 15, color: '#1A1C1C' }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, color: '#767683', marginTop: 1 }}>
              {subtitle}
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  )
}

// ─── Transaction type badge ────────────────────────────────────────────────────
function TxBadge({ type, t: propT }) {
  const { t: hookT } = useTranslation()
  const t = typeof propT === 'function' ? propT : hookT
  const map = {
    earned:   { label: t('loyalty.txEarned', 'مكتسب'),   color: '#1B6D24', bg: 'rgba(27,109,36,0.08)',   icon: TrendingUp },
    redeemed: { label: t('loyalty.txRedeemed', 'مستخدم'),  color: '#FF6D00', bg: 'rgba(255,109,0,0.08)',   icon: TrendingDown },
    adjusted: { label: t('loyalty.txAdjusted', 'معدّل'),   color: '#000666', bg: 'rgba(0,6,102,0.08)',      icon: Settings2 },
    expired:  { label: t('loyalty.txExpired', 'منتهي'),   color: '#767683', bg: 'rgba(118,118,131,0.08)', icon: AlertCircle },
  }
  const cfg = map[type] || map.adjusted
  const Icon = cfg.icon
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 8,
      background: cfg.bg, color: cfg.color,
      fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 11,
    }}>
      <Icon size={11} />{cfg.label}
    </span>
  )
}

// ─── Settings section ──────────────────────────────────────────────────────────
function SettingsSection() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data: settings, isLoading } = useQuery({
    queryKey: ['loyalty-settings'],
    queryFn: loyaltyService.getSettings,
  })

  const [form, setForm] = useState(null)

  // sync once loaded
  if (settings && form === null) {
    setForm({
      isEnabled:             settings.isEnabled ?? true,
      earnRatePercent:       String(settings.earnRatePercent ?? ''),
      pointValue:            String(settings.pointValue ?? ''),
      minRedemptionPoints:   String(settings.minRedemptionPoints ?? ''),
      maxRedemptionPercent:  String(settings.maxRedemptionPercent ?? ''),
      pointsExpireAfterDays: String(settings.pointsExpireAfterDays ?? '365'),
    })
  }

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }))

  const updateMut = useMutation({
    mutationFn: (payload) => loyaltyService.updateSettings(payload),
    onSuccess: () => { toast.success(t('loyalty.saveSuccess')); qc.invalidateQueries({ queryKey: ['loyalty-settings'] }) },
    onError: (e) => toast.error(e?.response?.data?.message || t('products.saveFailed')),
  })

  const expireMut = useMutation({
    mutationFn: () => loyaltyService.triggerExpirePoints(),
    onSuccess: (res) => toast.success(t('loyalty.expireSuccess', { points: res.totalExpired, accounts: res.accountsAffected })),
    onError: (e) => toast.error(e?.response?.data?.message || t('loyalty.expireError')),
  })

  const handleSave = () => {
    if (!form) return
    updateMut.mutate({
      isEnabled:             form.isEnabled,
      earnRatePercent:       parseFloat(form.earnRatePercent) || 0,
      pointValue:            parseFloat(form.pointValue) || 0,
      minRedemptionPoints:   parseInt(form.minRedemptionPoints) || 0,
      maxRedemptionPercent:  parseFloat(form.maxRedemptionPercent) || 0,
      pointsExpireAfterDays: parseInt(form.pointsExpireAfterDays) || 0,
    })
  }

  if (isLoading || !form) return <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><LoadingSpinner /></div>

  return (
    <SectionCard icon={SlidersHorizontal} title={t('loyalty.settingsTitle')} subtitle={t('loyalty.settingsSubtitle')}>

      {/* Enable toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', borderRadius: 14, marginBottom: 20,
        background: form.isEnabled ? 'rgba(27,109,36,0.05)' : '#F8F8FB',
        border: `1px solid ${form.isEnabled ? 'rgba(27,109,36,0.18)' : '#EBEBEB'}`,
        transition: 'all 0.2s',
      }}>
        <div>
          <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 14, color: '#1A1C1C' }}>
            {t('loyalty.enableToggleLabel')}
          </div>
          <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, color: '#767683', marginTop: 2 }}>
            {form.isEnabled ? t('loyalty.enabledDesc') : t('loyalty.disabledDesc')}
          </div>
        </div>
        <button
          onClick={() => set('isEnabled', !form.isEnabled)}
          style={{
            width: 52, height: 28, borderRadius: 14,
            background: form.isEnabled ? '#1B6D24' : '#C4C4C4',
            border: 'none', cursor: 'pointer', position: 'relative',
            transition: 'background 0.25s', flexShrink: 0,
            boxShadow: form.isEnabled ? '0 4px 12px rgba(27,109,36,0.3)' : 'none',
          }}
        >
          <span style={{
            position: 'absolute', top: 3,
            right: form.isEnabled ? 3 : undefined,
            left:  form.isEnabled ? undefined : 3,
            width: 22, height: 22, borderRadius: '50%',
            background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            transition: 'all 0.25s', display: 'block',
          }} />
        </button>
      </div>

      {/* Fields grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>

        <div>
          <label style={LBL}>{t('loyalty.earnRateLabel')}</label>
          <div style={{ position: 'relative' }}>
            <input
              type="number" min="0" max="100" step="0.1"
              value={form.earnRatePercent}
              onChange={(e) => set('earnRatePercent', e.target.value)}
              placeholder="5"
              dir="ltr"
              style={{ ...INP, paddingLeft: 40 }}
            />
            <span style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: 12, color: '#767683', fontSize: 13, fontFamily: 'Cairo, sans-serif', fontWeight: 700 }}>%</span>
          </div>
          <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683', marginTop: 4 }}>{t('loyalty.earnRateHint')}</p>
        </div>

        <div>
          <label style={LBL}>{t('loyalty.pointValueLabel')}</label>
          <input
            type="number" min="0" step="0.01"
            value={form.pointValue}
            onChange={(e) => set('pointValue', e.target.value)}
            placeholder="0.10"
            dir="ltr"
            style={INP}
          />
          <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683', marginTop: 4 }}>{t('loyalty.pointValueHint')}</p>
        </div>

        <div>
          <label style={LBL}>{t('loyalty.minRedemptionLabel')}</label>
          <input
            type="number" min="0"
            value={form.minRedemptionPoints}
            onChange={(e) => set('minRedemptionPoints', e.target.value)}
            placeholder="100"
            dir="ltr"
            style={INP}
          />
          <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683', marginTop: 4 }}>{t('loyalty.minRedemptionHint')}</p>
        </div>

        <div>
          <label style={LBL}>{t('loyalty.maxRedemptionLabel')}</label>
          <div style={{ position: 'relative' }}>
            <input
              type="number" min="0" max="100" step="1"
              value={form.maxRedemptionPercent}
              onChange={(e) => set('maxRedemptionPercent', e.target.value)}
              placeholder="30"
              dir="ltr"
              style={{ ...INP, paddingLeft: 40 }}
            />
            <span style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: 12, color: '#767683', fontSize: 13, fontFamily: 'Cairo, sans-serif', fontWeight: 700 }}>%</span>
          </div>
          <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683', marginTop: 4 }}>{t('loyalty.maxRedemptionHint')}</p>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <label style={LBL}>{t('loyalty.expiryDaysLabel')}</label>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <input
                type="number" min="0"
                value={form.pointsExpireAfterDays}
                onChange={(e) => set('pointsExpireAfterDays', e.target.value)}
                placeholder="365"
                dir="ltr"
                style={INP}
              />
              <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683', marginTop: 4 }}>
                {parseInt(form.pointsExpireAfterDays) === 0
                  ? t('loyalty.expiryNever')
                  : t('loyalty.expiryHint', { days: form.pointsExpireAfterDays, months: Math.round(parseInt(form.pointsExpireAfterDays) / 30) })}
              </p>
            </div>
            <button
              onClick={() => expireMut.mutate()}
              disabled={expireMut.isPending}
              title={t('loyalty.runExpiryTitle')}
              style={{
                marginTop: 1, display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 16px', borderRadius: 10, border: 'none', flexShrink: 0,
                fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 12,
                cursor: expireMut.isPending ? 'not-allowed' : 'pointer',
                opacity: expireMut.isPending ? 0.6 : 1,
                background: 'rgba(186,26,26,0.08)', color: '#BA1A1A',
              }}
            >
              {expireMut.isPending ? '...' : t('loyalty.runExpiryBtn')}
            </button>
          </div>
        </div>

      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <button
          onClick={handleSave}
          disabled={updateMut.isPending}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '11px 28px', borderRadius: 12,
            fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 14,
            color: '#fff', background: '#000666', border: 'none',
            cursor: updateMut.isPending ? 'not-allowed' : 'pointer',
            opacity: updateMut.isPending ? 0.6 : 1,
            boxShadow: '0 8px 24px rgba(0,6,102,0.2)',
          }}
        >
          <Save size={15} />
          {updateMut.isPending ? t('orders.saving') : t('settings.saveSettingsBtn')}
        </button>
      </div>
    </SectionCard>
  )
}

// ─── Redeemable categories section ────────────────────────────────────────────
function RedeemableCategoriesSection() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const qc = useQueryClient()

  // Fetch current settings (has redeemableCategories list)
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['loyalty-settings'],
    queryFn: loyaltyService.getSettings,
  })

  // Fetch all categories (flat tree)
  const { data: categoryTree, isLoading: catsLoading } = useQuery({
    queryKey: ['categories-tree'],
    queryFn: categoriesService.getTree,
  })

  // Flatten tree into a single list
  const allCategories = (() => {
    if (!categoryTree) return []
    const flat = []
    const traverse = (nodes) => {
      nodes.forEach((n) => {
        flat.push({ id: n.id, name: n.name })
        if (n.children?.length) traverse(n.children)
      })
    }
    traverse(Array.isArray(categoryTree) ? categoryTree : [categoryTree])
    return flat
  })()

  const redeemableIds = new Set((settings?.redeemableCategories || []).map((r) => r.categoryId))

  const addMut = useMutation({
    mutationFn: (categoryId) => loyaltyService.addRedeemableCategory(categoryId),
    onSuccess: () => {
      toast.success(t('loyalty.addCategorySuccess'))
      qc.invalidateQueries({ queryKey: ['loyalty-settings'] })
    },
    onError: (e) => toast.error(e?.response?.data?.message || t('loyalty.addCategoryError')),
  })

  const removeMut = useMutation({
    mutationFn: (categoryId) => loyaltyService.removeRedeemableCategory(categoryId),
    onSuccess: () => {
      toast.success(t('loyalty.removeCategorySuccess'))
      qc.invalidateQueries({ queryKey: ['loyalty-settings'] })
    },
    onError: (e) => toast.error(e?.response?.data?.message || t('loyalty.removeCategoryError')),
  })

  const toggle = (cat) => {
    if (addMut.isPending || removeMut.isPending) return
    if (redeemableIds.has(cat.id)) {
      removeMut.mutate(cat.id)
    } else {
      addMut.mutate(cat.id)
    }
  }

  const isLoading = settingsLoading || catsLoading

  return (
    <SectionCard
      icon={Tag}
      title={t('loyalty.redeemableCatsTitle')}
      subtitle={t('loyalty.redeemableCatsSubtitle')}
      accent="#7C3AED"
    >
      {/* Info banner */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '10px 14px', borderRadius: 12, marginBottom: 20,
        background: redeemableIds.size === 0 ? 'rgba(27,109,36,0.06)' : 'rgba(124,58,237,0.06)',
        border: `1px solid ${redeemableIds.size === 0 ? 'rgba(27,109,36,0.2)' : 'rgba(124,58,237,0.2)'}`,
      }}>
        <Info size={15} style={{ color: redeemableIds.size === 0 ? '#1B6D24' : '#7C3AED', marginTop: 2, flexShrink: 0 }} />
        <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13, color: '#454652', margin: 0, lineHeight: 1.6 }}>
          {redeemableIds.size === 0
            ? t('loyalty.emptyListInfo')
            : t('loyalty.limitedListInfo', { count: redeemableIds.size })}
        </p>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <LoadingSpinner />
        </div>
      ) : allCategories.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 0', fontFamily: 'Tajawal, sans-serif', fontSize: 14, color: '#767683' }}>
          {t('loyalty.noCategoriesInSystem')}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
          {allCategories.map((cat) => {
            const active = redeemableIds.has(cat.id)
            const busy = (addMut.isPending && addMut.variables === cat.id) ||
                         (removeMut.isPending && removeMut.variables === cat.id)
            return (
              <button
                key={cat.id}
                onClick={() => toggle(cat)}
                disabled={busy}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '11px 14px', borderRadius: 12,
                  border: `1.5px solid ${active ? '#7C3AED' : '#E2E2E2'}`,
                  background: active ? 'rgba(124,58,237,0.06)' : '#FAFAFA',
                  cursor: busy ? 'wait' : 'pointer',
                  textAlign: 'right', transition: 'all 0.18s',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {active
                  ? <CheckCircle2 size={17} style={{ color: '#7C3AED', flexShrink: 0 }} />
                  : <Circle size={17} style={{ color: '#C4C4C4', flexShrink: 0 }} />
                }
                <span style={{
                  fontFamily: 'Tajawal, sans-serif', fontSize: 13,
                  color: active ? '#7C3AED' : '#454652',
                  fontWeight: active ? 700 : 400,
                  flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {typeof cat.name === 'object' ? (cat.name?.ar || cat.name?.en || '') : (cat.name || '')}
                </span>
              </button>
            )
          })}
        </div>
      )}

      {/* Quick actions */}
      {!isLoading && allCategories.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button
            onClick={() => allCategories.forEach((c) => !redeemableIds.has(c.id) && addMut.mutate(c.id))}
            disabled={redeemableIds.size === allCategories.length || addMut.isPending}
            style={{
              padding: '7px 16px', borderRadius: 10, border: 'none',
              fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 12,
              cursor: 'pointer', background: 'rgba(124,58,237,0.08)', color: '#7C3AED',
              opacity: redeemableIds.size === allCategories.length || addMut.isPending ? 0.4 : 1,
            }}
          >
            تحديد الكل
          </button>
          <button
            onClick={() => [...redeemableIds].forEach((id) => removeMut.mutate(id))}
            disabled={redeemableIds.size === 0 || removeMut.isPending}
            style={{
              padding: '7px 16px', borderRadius: 10, border: 'none',
              fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 12,
              cursor: 'pointer', background: 'rgba(186,26,26,0.08)', color: '#BA1A1A',
              opacity: redeemableIds.size === 0 || removeMut.isPending ? 0.4 : 1,
            }}
          >
            إلغاء الكل
          </button>
        </div>
      )}
    </SectionCard>
  )
}

// ─── User search + account viewer ─────────────────────────────────────────────
function UserLookupSection() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounce(searchInput, 400)
  const [selectedUser, setSelectedUser]   = useState(null)
  const [txPage, setTxPage]               = useState(1)
  const [adjustForm, setAdjustForm]       = useState({ open: false, points: '', description: '' })

  // Search users
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users-search', debouncedSearch],
    queryFn: () => usersService.getAll({ search: debouncedSearch, limit: 6 }),
    enabled: debouncedSearch.length >= 2,
  })
  const usersList = usersData?.data || []

  // Selected user account
  const { data: account, isLoading: acctLoading } = useQuery({
    queryKey: ['loyalty-account', selectedUser?.id, txPage],
    queryFn: () => loyaltyService.getUserAccount(selectedUser.id, { page: txPage, limit: 10 }),
    enabled: !!selectedUser,
  })

  const adjustMut = useMutation({
    mutationFn: ({ points, description }) =>
      loyaltyService.adjustPoints(selectedUser.id, points, description),
    onSuccess: (res) => {
      toast.success(`تم التعديل — الرصيد الجديد: ${res.balance} نقطة`)
      qc.invalidateQueries({ queryKey: ['loyalty-account', selectedUser?.id] })
      setAdjustForm({ open: false, points: '', description: '' })
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل التعديل'),
  })

  const handleAdjust = () => {
    const pts = parseInt(adjustForm.points)
    if (!pts || pts === 0) { toast.error('أدخل عدد نقاط صالح (موجب أو سالب)'); return }
    if (!adjustForm.description.trim()) { toast.error('أدخل وصفاً للتعديل'); return }
    adjustMut.mutate({ points: pts, description: adjustForm.description.trim() })
  }

  const transactions = account?.transactions || []
  const meta = account?.meta || {}

  return (
    <SectionCard icon={UserCheck} title="حساب نقاط المستخدم" subtitle="ابحث عن مستخدم لعرض رصيده وسجل معاملاته" accent="#FF6D00">

      {/* Search box */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <Search size={16} style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: 14, color: '#767683', pointerEvents: 'none' }} />
        <input
          value={searchInput}
          onChange={(e) => { setSearchInput(e.target.value); setSelectedUser(null) }}
          placeholder="ابحث بالاسم أو البريد الإلكتروني..."
          style={{ ...INP, paddingRight: 42 }}
        />
      </div>

      {/* Search results dropdown */}
      {debouncedSearch.length >= 2 && !selectedUser && (
        <div style={{ marginBottom: 16 }}>
          {usersLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}><LoadingSpinner /></div>
          ) : usersList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px 0', fontFamily: 'Tajawal, sans-serif', fontSize: 14, color: '#767683' }}>
              لا يوجد مستخدمون مطابقون
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {usersList.map((u) => (
                <button
                  key={u.id}
                  onClick={() => { setSelectedUser(u); setSearchInput(u.name); setTxPage(1) }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 12,
                    border: '1px solid #EBEBEB', background: '#FAFAFA',
                    cursor: 'pointer', textAlign: 'right', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#000666'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#EBEBEB'}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: '#000666', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 14,
                  }}>
                    {u.name?.charAt(0) || '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 14, color: '#1A1C1C' }}>{u.name}</div>
                    <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, color: '#767683' }}>{u.email}</div>
                  </div>
                  <span style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683' }}>{u.role}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Account view */}
      {selectedUser && (
        <>
          {acctLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><LoadingSpinner /></div>
          ) : account ? (
            <>
              {/* Stats row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                  { label: 'الرصيد الحالي', value: account.balance, icon: Coins, color: '#000666' },
                  { label: 'إجمالي المكتسب', value: account.totalEarned, icon: TrendingUp, color: '#1B6D24' },
                  { label: 'إجمالي المستخدم', value: account.totalRedeemed, icon: TrendingDown, color: '#FF6D00' },
                ].map(({ label, value, icon: Icon, color }) => (
                  <div key={label} style={{
                    borderRadius: 14, padding: '14px 16px',
                    background: `${color}0A`,
                    border: `1px solid ${color}1A`,
                    textAlign: 'center',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
                      <Icon size={20} style={{ color }} />
                    </div>
                    <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 900, fontSize: 22, color, lineHeight: 1 }}>
                      {value ?? 0}
                    </div>
                    <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683', marginTop: 4 }}>
                      {label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Adjust button */}
              {!adjustForm.open && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <button
                    onClick={() => setAdjustForm({ open: true, type: 'add', points: '', description: '' })}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 16px', borderRadius: 10, border: 'none',
                      fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13,
                      cursor: 'pointer', background: 'rgba(27,109,36,0.08)', color: '#1B6D24',
                    }}
                  >
                    <Plus size={14} />إضافة نقاط
                  </button>
                  <button
                    onClick={() => setAdjustForm({ open: true, type: 'sub', points: '', description: '' })}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 16px', borderRadius: 10, border: 'none',
                      fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13,
                      cursor: 'pointer', background: 'rgba(186,26,26,0.08)', color: '#BA1A1A',
                    }}
                  >
                    <Minus size={14} />خصم نقاط
                  </button>
                </div>
              )}

              {/* Adjust form */}
              {adjustForm.open && (
                <div style={{
                  borderRadius: 14, padding: 16, marginBottom: 16,
                  background: adjustForm.type === 'add' ? 'rgba(27,109,36,0.05)' : 'rgba(186,26,26,0.05)',
                  border: `1px solid ${adjustForm.type === 'add' ? 'rgba(27,109,36,0.2)' : 'rgba(186,26,26,0.2)'}`,
                }}>
                  <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: '#1A1C1C', marginBottom: 12 }}>
                    {adjustForm.type === 'add' ? '➕ إضافة نقاط' : '➖ خصم نقاط'}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 12 }}>
                    <div>
                      <label style={LBL}>عدد النقاط</label>
                      <input
                        type="number" min="1"
                        value={adjustForm.points}
                        onChange={(e) => setAdjustForm((p) => ({ ...p, points: e.target.value }))}
                        placeholder="50"
                        dir="ltr"
                        style={INP}
                      />
                    </div>
                    <div>
                      <label style={LBL}>سبب التعديل</label>
                      <input
                        value={adjustForm.description}
                        onChange={(e) => setAdjustForm((p) => ({ ...p, description: e.target.value }))}
                        placeholder="مثال: تعويض مشكلة في الطلب"
                        style={INP}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={handleAdjust}
                      disabled={adjustMut.isPending}
                      style={{
                        padding: '9px 20px', borderRadius: 10, border: 'none',
                        fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13,
                        cursor: adjustMut.isPending ? 'not-allowed' : 'pointer',
                        opacity: adjustMut.isPending ? 0.6 : 1,
                        color: '#fff',
                        background: adjustForm.type === 'add' ? '#1B6D24' : '#BA1A1A',
                      }}
                    >
                      {adjustMut.isPending ? '...' : 'تأكيد'}
                    </button>
                    <button
                      onClick={() => setAdjustForm({ open: false, points: '', description: '' })}
                      style={{
                        padding: '9px 20px', borderRadius: 10, border: 'none',
                        fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13,
                        cursor: 'pointer', background: '#F3F3F3', color: '#454652',
                      }}
                    >
                      إلغاء
                    </button>
                  </div>
                </div>
              )}

              {/* Transactions table */}
              <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: '#454652', marginBottom: 10 }}>
                سجل المعاملات
              </div>

              {transactions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', fontFamily: 'Tajawal, sans-serif', fontSize: 14, color: '#767683' }}>
                  لا توجد معاملات بعد
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {transactions.map((tx) => (
                      <div key={tx.id} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 14px', borderRadius: 12,
                        border: '1px solid #F3F3F3', background: '#FAFAFA',
                      }}>
                        <TxBadge type={tx.type} t={t} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13, color: '#454652' }}>
                            {tx.description}
                          </div>
                          {tx.order && (
                            <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683', marginTop: 2 }}>
                              طلب: {tx.order.orderNumber}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'left', flexShrink: 0 }}>
                          <div style={{
                            fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 14,
                            color: tx.points > 0 ? '#1B6D24' : '#BA1A1A',
                          }}>
                            {tx.points > 0 ? '+' : ''}{tx.points}
                          </div>
                          <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683' }}>
                            رصيد: {tx.balanceAfter}
                          </div>
                        </div>
                        <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683', textAlign: 'left', minWidth: 70 }}>
                          {new Date(tx.createdAt).toLocaleDateString('ar-EG', { day: '2-digit', month: 'short', year: '2-digit' })}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {meta.totalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                      <button
                        onClick={() => setTxPage((p) => Math.max(1, p - 1))}
                        disabled={!meta.hasPrev}
                        style={{
                          width: 32, height: 32, borderRadius: 8, border: 'none',
                          background: meta.hasPrev ? 'rgba(0,6,102,0.08)' : '#F3F3F3',
                          color: meta.hasPrev ? '#000666' : '#C4C4C4',
                          cursor: meta.hasPrev ? 'pointer' : 'default',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <ChevronRight size={14} />
                      </button>
                      <span style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13, color: '#767683' }}>
                        {txPage} / {meta.totalPages}
                      </span>
                      <button
                        onClick={() => setTxPage((p) => Math.min(meta.totalPages, p + 1))}
                        disabled={!meta.hasNext}
                        style={{
                          width: 32, height: 32, borderRadius: 8, border: 'none',
                          background: meta.hasNext ? 'rgba(0,6,102,0.08)' : '#F3F3F3',
                          color: meta.hasNext ? '#000666' : '#C4C4C4',
                          cursor: meta.hasNext ? 'pointer' : 'default',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <ChevronLeft size={14} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '24px 0', fontFamily: 'Tajawal, sans-serif', fontSize: 14, color: '#767683' }}>
              هذا المستخدم ليس لديه حساب نقاط بعد
            </div>
          )}
        </>
      )}
    </SectionCard>
  )
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function LoyaltyPage() {
  return (
    <div className="space-y-5">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: '#000666', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 20px rgba(0,6,102,0.3)',
        }}>
          <Gift size={20} />
        </div>
        <div>
          <h1 style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 900, fontSize: 20, color: '#1A1C1C', margin: 0 }}>
            برنامج النقاط
          </h1>
          <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13, color: '#767683', margin: 0 }}>
            إدارة نقاط الولاء وحسابات العملاء
          </p>
        </div>
      </div>

      <SettingsSection />
      <RedeemableCategoriesSection />
      <UserLookupSection />
    </div>
  )
}
